import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Stack,
    Chip,
    Alert,
    CircularProgress,
    Tooltip,
    Grid
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    History as HistoryIcon,
    Code as CodeIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import apiService from '../services/api.service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { showSnackbar } from '../store/slices/uiSlice';
import CodeEditor from '../components/common/CodeEditor';
import ScheduleEditor from '../components/common/ScheduleEditor';
import SidePanel from '../components/common/SidePanel';

interface TransactionSyncConfig {
    syncid: number;
    syncname: string;
    description?: string;
    fromendpoint: string;
    toendpoint: string;
    syncscript: string;
    scheduleinterval: string;
    isactive: boolean;
    lastruntime?: string;
    lastrunstatus?: string;
    lastrunmessage?: string;
    companyid?: number | null;
    syncfromdate?: string;
}

interface Endpoint {
    value: string;
    label: string;
}

interface CompanyRow {
    CompanyID: number;
    CompanyCode: string;
    CompanyName: string;
    DatabaseName: string;
    IsActive: boolean;
}

const TransactionSync: React.FC = () => {
    const dispatch = useDispatch();
    // Sync scripts run on the server, so only an Admin may create, edit or
    // delete a configuration (the API enforces this). Other users of this page
    // can view a configuration read-only, run it and read its logs.
    const user = useSelector((state: RootState) => state.auth.user);
    const isAdmin = String(user?.role || '') === 'Admin';
    const [loading, setLoading] = useState(false);
    const [syncs, setSyncs] = useState<TransactionSyncConfig[]>([]);
    const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
    const [companies, setCompanies] = useState<CompanyRow[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [logsDialogOpen, setLogsDialogOpen] = useState(false);
    const [editingSync, setEditingSync] = useState<TransactionSyncConfig | null>(null);
    const [logs, setLogs] = useState<any[]>([]);
    const [filterCompanyId, setFilterCompanyId] = useState<number | ''>('');
    const [executingSync, setExecutingSync] = useState<number | null>(null);
    const [executeProgressDialog, setExecuteProgressDialog] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        syncName: '',
        description: '',
        fromEndpoint: '',
        toEndpoint: '',
        syncScript: '',
        scheduleInterval: '0 */6 * * *', // Every 6 hours by default
        syncFromDate: new Date().toISOString().split('T')[0], // Today's date by default
        isActive: true,
        companyId: '' as number | ''
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [syncsRes, endpointsRes, companiesRes] = await Promise.all([
                apiService.getTransactionSyncs(filterCompanyId === '' ? undefined : Number(filterCompanyId)),
                apiService.getAvailableEndpoints(),
                apiService.getCompanies(true)
            ]);

            setSyncs(syncsRes?.data || []);
            setEndpoints(endpointsRes?.data || []);

            // Normalize companies payload shape (backend returns { success, data: [...] })
            const rawCompanies = Array.isArray(companiesRes) ? companiesRes : (companiesRes?.data || []);
            setCompanies(rawCompanies.map((c: any) => ({
                CompanyID: c.CompanyID ?? c.companyid,
                CompanyCode: c.CompanyCode ?? c.companycode,
                CompanyName: c.CompanyName ?? c.companyname,
                DatabaseName: c.DatabaseName ?? c.databasename,
                IsActive: c.IsActive ?? c.isactive,
            })));
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to load data',
                severity: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, filterCompanyId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleOpenDialog = (sync?: TransactionSyncConfig) => {
        if (sync) {
            setEditingSync(sync);
            setFormData({
                syncName: sync.syncname,
                description: sync.description || '',
                fromEndpoint: sync.fromendpoint,
                toEndpoint: sync.toendpoint,
                syncScript: sync.syncscript,
                scheduleInterval: sync.scheduleinterval,
                syncFromDate: sync.syncfromdate || new Date().toISOString().split('T')[0],
                isActive: sync.isactive,
                companyId: sync.companyid ?? ''
            });
        } else {
            setEditingSync(null);
            setFormData({
                syncName: '',
                description: '',
                fromEndpoint: '',
                toEndpoint: '',
                // eslint-disable-next-line no-template-curly-in-string
                syncScript: '// Example GRPO to Journal Entry dimension update script\n// Available variables: sapb1Service, fromEndpoint, toEndpoint, companyId, pool, logger\n\n// Fetch GRPOs\nconst grpos = await sapb1Service.get(`/${fromEndpoint}?$filter=DocDate ge \'2025-01-01\'`);\nconst grpoList = grpos.data.value || [];\n\nfor (const grpo of grpoList) {\n  try {\n    // Get related journal entry\n    const jeQuery = `/${toEndpoint}?$filter=TransactionCode eq \'${grpo.DocEntry}\'`;\n    const jeRes = await sapb1Service.get(jeQuery);\n    const journalEntries = jeRes.data.value || [];\n    \n    if (journalEntries.length > 0) {\n      const je = journalEntries[0];\n      \n      // Update dimensions - example logic\n      const updateData = {\n        JournalEntryLines: je.JournalEntryLines.map(line => ({\n          ...line,\n          CostingCode: grpo.Project || line.CostingCode,\n          CostingCode2: grpo.DistributionRule || line.CostingCode2\n        }))\n      };\n      \n      // Patch journal entry\n      await sapb1Service.patch(\n        `/${toEndpoint}(${je.JdtNum})`,\n        updateData\n      );\n      \n      context.recordsProcessed++;\n      logger.info(`Updated JE ${je.JdtNum} for GRPO ${grpo.DocEntry}`);\n    }\n  } catch (err) {\n    context.recordsFailed++;\n    logger.error(`Failed to process GRPO ${grpo.DocEntry}:`, err);\n  }\n}',
                scheduleInterval: '0 */6 * * *',
                syncFromDate: new Date().toISOString().split('T')[0],
                isActive: true,
                companyId: ''
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingSync(null);
    };

    const handleSave = async () => {
        if (!isAdmin) return;
        try {
            if (!formData.syncName || !formData.fromEndpoint || !formData.toEndpoint || !formData.syncScript) {
                dispatch(showSnackbar({
                    message: 'Please fill in all required fields',
                    severity: 'error'
                }));
                return;
            }

            const syncData = {
                syncName: formData.syncName,
                description: formData.description,
                fromEndpoint: formData.fromEndpoint,
                toEndpoint: formData.toEndpoint,
                syncScript: formData.syncScript,
                scheduleInterval: formData.scheduleInterval,
                syncFromDate: formData.syncFromDate,
                isActive: formData.isActive,
                companyId: formData.companyId === '' ? null : Number(formData.companyId)
            };

            if (editingSync) {
                await apiService.updateTransactionSync(editingSync.syncid, syncData);
                dispatch(showSnackbar({
                    message: 'Sync configuration updated successfully',
                    severity: 'success'
                }));
            } else {
                await apiService.createTransactionSync(syncData);
                dispatch(showSnackbar({
                    message: 'Sync configuration created successfully',
                    severity: 'success'
                }));
            }

            handleCloseDialog();
            loadData();
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to save sync configuration',
                severity: 'error'
            }));
        }
    };

    const handleDelete = async (syncId: number) => {
        if (!isAdmin) return;
        if (!window.confirm('Are you sure you want to delete this sync configuration?')) {
            return;
        }

        try {
            await apiService.deleteTransactionSync(syncId);
            dispatch(showSnackbar({
                message: 'Sync configuration deleted successfully',
                severity: 'success'
            }));
            loadData();
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to delete sync configuration',
                severity: 'error'
            }));
        }
    };

    const handleExecute = async (syncId: number) => {
        setExecutingSync(syncId);
        setExecuteProgressDialog(true);

        try {
            await apiService.executeTransactionSync(syncId);

            dispatch(showSnackbar({
                message: 'Sync executed successfully',
                severity: 'success'
            }));

            loadData();
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to execute sync',
                severity: 'error'
            }));
        } finally {
            setExecutingSync(null);
            setExecuteProgressDialog(false);
        }
    };

    const handleViewLogs = async (syncId: number) => {
        try {
            const response = await apiService.getTransactionSyncLogs(syncId, 50);
            setLogs(response?.data || []);
            setLogsDialogOpen(true);
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to load logs',
                severity: 'error'
            }));
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'syncname',
            headerName: 'Sync Name',
            width: 200,
            flex: 1
        },
        {
            field: 'companyid',
            headerName: 'Company',
            width: 150,
            renderCell: (params) => {
                const company = companies.find((c) => c.CompanyID === params.value);
                return company?.CompanyName || '-';
            }
        },
        {
            field: 'fromendpoint',
            headerName: 'From',
            width: 150
        },
        {
            field: 'toendpoint',
            headerName: 'To',
            width: 150
        },
        {
            field: 'scheduleinterval',
            headerName: 'Schedule',
            width: 120
        },
        {
            field: 'isactive',
            headerName: 'Active',
            width: 80,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Yes' : 'No'}
                    color={params.value ? 'success' : 'default'}
                    size="small"
                />
            )
        },
        {
            field: 'lastrunstatus',
            headerName: 'Last Status',
            width: 100,
            renderCell: (params) => params.value ? (
                <Chip
                    label={params.value}
                    color={params.value === 'Success' ? 'success' : 'error'}
                    size="small"
                />
            ) : null
        },
        {
            field: 'lastruntime',
            headerName: 'Last Run',
            width: 150,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : '-'
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            getActions: (params) => {
                const actions = [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Run Now">
                                {executingSync === params.row.syncid ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <PlayIcon />
                                )}
                            </Tooltip>
                        }
                        label="Execute"
                        onClick={() => handleExecute(params.row.syncid)}
                        disabled={executingSync === params.row.syncid}
                    />,
                    <GridActionsCellItem
                        icon={<Tooltip title="View Logs"><HistoryIcon /></Tooltip>}
                        label="Logs"
                        onClick={() => handleViewLogs(params.row.syncid)}
                        showInMenu
                    />,
                    <GridActionsCellItem
                        icon={<Tooltip title={isAdmin ? 'Edit' : 'View'}>{isAdmin ? <EditIcon /> : <ViewIcon />}</Tooltip>}
                        label={isAdmin ? 'Edit' : 'View'}
                        onClick={() => handleOpenDialog(params.row)}
                        showInMenu
                    />
                ];
                if (isAdmin) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
                            label="Delete"
                            onClick={() => handleDelete(params.row.syncid)}
                            showInMenu
                        />
                    );
                }
                return actions;
            }
        }
    ];

    return (
        <Box component="section" aria-labelledby="ts-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={{ xs: 1.5, sm: 0 }}
                    mb={3}
                >
                    <Typography
                        id="ts-heading"
                        variant="h5"
                        component="h1"
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
                    >
                        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} aria-hidden="true" />
                        Transaction Sync
                    </Typography>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            New Sync
                        </Button>
                    )}
                </Stack>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Configure custom scripts to sync data between SAP B1 endpoints. Example: Update GRPO dimensions to Journal Entries.
                </Alert>

                <Box sx={{ mb: 3 }}>
                    <FormControl sx={{ minWidth: 300 }}>
                        <InputLabel id="company-filter-label">Filter by Company</InputLabel>
                        <Select
                            labelId="company-filter-label"
                            value={filterCompanyId}
                            label="Filter by Company"
                            onChange={(e) => setFilterCompanyId(e.target.value as number | '')}
                        >
                            <MenuItem value="">
                                <em>All Companies</em>
                            </MenuItem>
                            {companies.map((c) => (
                                <MenuItem key={c.CompanyID} value={c.CompanyID}>
                                    {c.CompanyName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={syncs}
                        columns={columns}
                        getRowId={(row) => row.syncid}
                        autoHeight
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 }
                            }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        disableRowSelectionOnClick
                    />
                )}
            </Paper>

            {/* Create/Edit Side Panel */}
            <SidePanel
                open={dialogOpen}
                onClose={handleCloseDialog}
                title={!isAdmin ? 'View Sync Configuration' : editingSync ? 'Edit Sync Configuration' : 'Create New Sync Configuration'}
                icon={<CodeIcon sx={{ fontSize: '1.5rem' }} />}
                width={1000}
                actions={
                    !isAdmin ? (
                        <Button onClick={handleCloseDialog} sx={{ color: '#cccccc' }}>Close</Button>
                    ) : (
                    <>
                        <Button onClick={handleCloseDialog} sx={{ color: '#cccccc' }}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!formData.syncName || !formData.fromEndpoint || !formData.toEndpoint || !formData.syncScript}
                            sx={{
                                backgroundColor: '#007acc',
                                '&:hover': {
                                    backgroundColor: '#005a9e'
                                }
                            }}
                        >
                            {editingSync ? 'Update' : 'Create'}
                        </Button>
                    </>
                    )
                }
            >
                {!isAdmin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Read-only: only an Admin can change sync configurations and scripts. You can run this sync and read its logs from the list.
                    </Alert>
                )}
                <Grid container spacing={2}>
                    {/* Left Column - Form Fields */}
                    <Grid item xs={12} md={5}>
                        {/* Read-only for non-Admins: the fieldset disables the native
                            inputs; pointer-events also blocks the MUI Select and
                            schedule pickers. The script editor below is readOnly. */}
                        <fieldset
                            disabled={!isAdmin}
                            style={{ border: 0, padding: 0, margin: 0, minWidth: 0, pointerEvents: isAdmin ? undefined : 'none' }}
                        >
                        <Stack spacing={1.5}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ fontSize: '0.8rem', color: '#cccccc' }}>Company Database</InputLabel>
                                    <Select
                                        value={formData.companyId}
                                        label="Company Database"
                                        onChange={(e) => setFormData({ ...formData, companyId: e.target.value as any })}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#3c3c3c',
                                            color: '#d4d4d4',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#555'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#007acc'
                                            },
                                            '& .MuiSelect-icon': {
                                                color: '#cccccc'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {companies.map((c) => (
                                            <MenuItem key={c.CompanyID} value={c.CompanyID}>
                                                {c.CompanyName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Sync Name"
                                    value={formData.syncName}
                                    onChange={(e) => setFormData({ ...formData, syncName: e.target.value })}
                                    required
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#3c3c3c',
                                            color: '#d4d4d4',
                                            '& fieldset': {
                                                borderColor: '#555'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#007acc'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#007acc'
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#cccccc',
                                            fontSize: '0.8rem'
                                        }
                                    }}
                                />

                                <TextField
                                    label="Description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#3c3c3c',
                                            color: '#d4d4d4',
                                            '& fieldset': {
                                                borderColor: '#555'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#007acc'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#007acc'
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#cccccc',
                                            fontSize: '0.8rem'
                                        }
                                    }}
                                />

                                <Stack direction="row" spacing={1}>
                                    <FormControl fullWidth required size="small">
                                        <InputLabel sx={{ fontSize: '0.8rem', color: '#cccccc' }}>From Endpoint</InputLabel>
                                        <Select
                                            value={formData.fromEndpoint}
                                            label="From Endpoint"
                                            onChange={(e) => setFormData({ ...formData, fromEndpoint: e.target.value })}
                                            sx={{
                                                backgroundColor: '#3c3c3c',
                                                color: '#d4d4d4',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#555'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#007acc'
                                                },
                                                '& .MuiSelect-icon': {
                                                    color: '#cccccc'
                                                }
                                            }}
                                        >
                                            {endpoints.map((ep) => (
                                                <MenuItem key={ep.value} value={ep.value}>
                                                    {ep.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth required size="small">
                                        <InputLabel sx={{ fontSize: '0.8rem', color: '#cccccc' }}>To Endpoint</InputLabel>
                                        <Select
                                            value={formData.toEndpoint}
                                            label="To Endpoint"
                                            onChange={(e) => setFormData({ ...formData, toEndpoint: e.target.value })}
                                            sx={{
                                                backgroundColor: '#3c3c3c',
                                                color: '#d4d4d4',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#555'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#007acc'
                                                },
                                                '& .MuiSelect-icon': {
                                                    color: '#cccccc'
                                                }
                                            }}
                                        >
                                            {endpoints.map((ep) => (
                                                <MenuItem key={ep.value} value={ep.value}>
                                                    {ep.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <ScheduleEditor
                                    value={formData.scheduleInterval}
                                    onChange={(val) => setFormData({ ...formData, scheduleInterval: val })}
                                    label="Schedule"
                                    fullWidth
                                />

                                <TextField
                                    label="Sync From Date"
                                    type="date"
                                    value={formData.syncFromDate}
                                    onChange={(e) => setFormData({ ...formData, syncFromDate: e.target.value })}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    helperText="Only sync records from this date onwards"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#3c3c3c',
                                            color: '#d4d4d4',
                                            '& fieldset': {
                                                borderColor: '#555'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#007acc'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#007acc'
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#cccccc',
                                            fontSize: '0.8rem'
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: '#858585',
                                            fontSize: '0.7rem'
                                        }
                                    }}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                            sx={{
                                                '& .MuiSwitch-switchBase': {
                                                    color: '#007acc'
                                                },
                                                '& .MuiSwitch-track': {
                                                    borderColor: '#555'
                                                }
                                            }}
                                        />
                                    }
                                    label="Active"
                                    sx={{ color: '#cccccc', fontSize: '0.875rem' }}
                                />
                            </Stack>
                        </fieldset>
                        </Grid>

                        {/* Right Column - Code Editor */}
                        <Grid item xs={12} md={7}>
                            <Stack spacing={1}>
                                <Typography variant="subtitle2" sx={{ color: '#cccccc', fontSize: '0.875rem', fontWeight: 600 }}>
                                    Sync Script (JavaScript)
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#858585', fontSize: '0.75rem' }}>
                                    Available: sapb1Service, fromEndpoint, toEndpoint, companyId, pool, logger, context
                                </Typography>
                                <CodeEditor
                                    value={formData.syncScript}
                                    onChange={(val) => setFormData({ ...formData, syncScript: val })}
                                    readOnly={!isAdmin}
                                    fontSize={13}
                                    minLines={20}
                                    maxLines={30}
                                    fullWidth
                                    helperText=""
                                    error={!formData.syncScript}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
            </SidePanel>

            {/* Logs Dialog */}
            <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Execution Logs</DialogTitle>
                <DialogContent>
                    {logs.length === 0 ? (
                        <Typography color="text.secondary">No logs available</Typography>
                    ) : (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            {logs.map((log) => (
                                <Paper key={log.logid} sx={{ p: 2, bgcolor: log.status === 'Failed' ? '#fff3f3' : '#f0f9ff' }}>
                                    <Stack direction="row" justifyContent="space-between" mb={1}>
                                        <Chip
                                            label={log.status}
                                            color={log.status === 'Success' ? 'success' : 'error'}
                                            size="small"
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(log.executiontime).toLocaleString()}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2">{log.message}</Typography>
                                    {log.recordsprocessed > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Processed: {log.recordsprocessed} | Failed: {log.recordsfailed || 0} | Duration: {log.executionduration}ms
                                        </Typography>
                                    )}
                                    {log.errordetails && (
                                        <Typography variant="caption" color="error" component="pre" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                            {log.errordetails}
                                        </Typography>
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Execute Progress Dialog */}
            <Dialog open={executeProgressDialog} disableEscapeKeyDown>
                <DialogContent sx={{ minWidth: 400, py: 4 }}>
                    <Stack spacing={3} alignItems="center">
                        <CircularProgress size={60} thickness={4} />
                        <Stack spacing={1} textAlign="center">
                            <Typography variant="h6">Executing Sync</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please wait while the sync is running. This may take several minutes...
                            </Typography>
                        </Stack>
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    You can track the progress in the backend console logs.
                                </Typography>
                            </Alert>
                        </Box>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TransactionSync;
